import { GridLink } from '@components/gridLink/GridLink';

import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { OverflowTooltip } from '@components/tooltip';
import { ColumnType } from '@models/quotes';

import { ShortDateFormat } from '@utils/date';
import { formatCurrency } from '@utils/number';
import { format } from 'date-fns-tz';

import { IndicationSearchResponse } from '@lio/api-types';
import { IndicationColumnField } from '@pages/Quotes/IndicationsTable/IndicationColumnField.enum';
import { EllipsisH } from '@assets/svgs';
import { MenuButton, MenuItemType } from '@components/menu';
import { QuoteColumnField } from '@pages/Quotes/QuotesTable/QuoteColumnField.enum';

export type IndicationRow = IndicationSearchResponse['items'][0];

export function getIndicationColumns({
  onOpenModal,
}: {
  onOpenModal: (params: { type: 'confirmDelete' | 'convertToQuote'; indication: IndicationRow }) => void;
}): TableGridBaseColDef<IndicationRow>[] {
  return [
    {
      field: IndicationColumnField.IndicationNumber,
      headerName: 'Indication #',
      type: ColumnType.String,
      flex: 0.4,
      minWidth: 130,
      renderCell: (params) => {
        return <GridLink label={params.row.referenceId} to={`/indication/${params.row.id}`} />;
      },
      mobileConfig: {
        mobileLockedColumn: true,
      },
    },
    {
      field: IndicationColumnField.AccountName,
      headerName: 'Account Name',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 150,
      valueGetter: (value, row) => row.customer?.name || '-',
      renderCell: (params) => {
        return <OverflowTooltip>{params.row.customer?.name || '-'}</OverflowTooltip>;
      },
    },
    {
      field: IndicationColumnField.Offering,
      headerName: 'Product',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      valueGetter: (_, row) => {
        if (row.suggestedInsuranceProducts && row.suggestedInsuranceProducts.length > 0) {
          const firstSuggested = row.suggestedInsuranceProducts[0];
          if (firstSuggested.offerings?.[0]?.label) return firstSuggested.offerings[0].label;
          else if (firstSuggested.name) return firstSuggested.name;
        }
        return row.offering?.label || row.offering?.insuranceProduct?.name || '-';
      },
    },
    {
      field: IndicationColumnField.IndicationAmount,
      headerName: 'Indication Amount',
      flex: 0.4,
      renderCell: (params) => {
        return formatCurrency(params.row.termPremium);
      },
    },
    {
      field: IndicationColumnField.EffectiveDate,
      headerName: 'Effective On',
      flex: 0.4,
      valueGetter: (value) => (value ? format(value, ShortDateFormat) : '-'),
    },
    {
      field: IndicationColumnField.CreatedAt,
      headerName: 'Created Date',
      flex: 0.4,
      valueGetter: (value) => format(value, ShortDateFormat),
    },
    {
      field: IndicationColumnField.CreatedBy,
      headerName: 'Created By',
      flex: 0.5,
      valueGetter: (_, row) => row.createdByUser.lastName + ', ' + row.createdByUser.firstName,
    },

    {
      field: QuoteColumnField.Menu,
      type: ColumnType.Action,
      width: 40,
      valueGetter: () => undefined,
      sortable: false,
      mobileConfig: {
        hidden: { xs: true },
        actionMenu: true,
      },
      renderCell: (params) => {
        return (
          <MenuButton
            key='menu-button'
            icon={<EllipsisH />}
            items={[
              {
                type: MenuItemType.Link,
                actionName: `/indication/${params.row.id}`,
                text: 'View Details',
              },
              {
                type: MenuItemType.Default,
                text: 'Continue To Quote',
                handleOnClick: () => {
                  onOpenModal({ type: 'convertToQuote', indication: params.row });
                },
              },
              {
                type: MenuItemType.Default,
                text: 'Delete Indication',
                handleOnClick: () => {
                  onOpenModal({ type: 'confirmDelete', indication: params.row });
                },
              },
            ]}
          />
        );
      },
    },
  ];
}
